"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useAuth } from "@/lib/auth";
import { trackPageView, identifyUser, resetUser } from "@/lib/analytics/posthog";
interface AnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Analytics side-effects (mounted by `AnalyticsProvider` below) that:
 * 1. Track page views on route changes (including the initial mount)
 * 2. Identify users when they log in
 *
 * Note: This is first-party analytics on a self-hosted PostHog instance.
 * No consent is required for GDPR compliance since data stays within
 * our EU infrastructure and is used only for our own analytics.
 *
 * Initialization-state gating is intentionally absent: the underlying
 * `trackPageView` / `identifyUser` helpers no-op when PostHog isn't
 * configured (production-disabled builds) or hasn't loaded yet, so we
 * can fire eagerly without a separate "ready" flag.
 *
 * The route-change effect fires on initial mount because the first
 * `pathname` change is `null → real pathname`. A previous version had
 * an additional mount-only effect that *also* fired `trackPageView()`,
 * which double-emitted every $pageview (once with `window.location.href`
 * encoded as `%20`, once with `searchParams.toString()` encoded as `+`).
 * Pageview-derived metrics were inflated ~19% across the board until
 * that init effect was removed.
 */
/**
 * Side-effect-only tracker. Reads `useSearchParams`, which Next requires to
 * live under a <Suspense> boundary. Isolated from `{children}` on purpose:
 * the boundary must wrap ONLY this null-rendering component, never the page
 * tree. A <Suspense> above the page flushes a 200 HTML shell before the page
 * resolves, which locks the HTTP status at 200 — so a page-level notFound()
 * (e.g. the catch-all `app/[...slug]`) degrades to a soft-404 (200 body that
 * says "Not Found"). Keeping the boundary here lets pages return a real 404.
 * See `AnalyticsProvider` and Next.js issue #70447.
 */
function AnalyticsTracker() {
  const $ = _c(8);
  const {
    user,
    isAuthenticated
  } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const previousPathname = useRef(null);
  const wasAuthenticated = useRef(false);
  let t0;
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t0 = () => {
      if (previousPathname.current === pathname) {
        return;
      }
      previousPathname.current = pathname;
      const url = searchParams?.toString() ? `${pathname}?${searchParams.toString()}` : pathname;
      trackPageView(window.location.origin + url);
    };
    t1 = [pathname, searchParams];
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== isAuthenticated || $[5] !== user) {
    t2 = () => {
      if (isAuthenticated && user) {
        wasAuthenticated.current = true;
        identifyUser(user.id, {
          email: user.email,
          created_at: user.created_at
        });
      } else {
        if (wasAuthenticated.current) {
          wasAuthenticated.current = false;
          resetUser();
        }
      }
    };
    t3 = [isAuthenticated, user];
    $[4] = isAuthenticated;
    $[5] = user;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  return null;
}

/**
 * Mounts the analytics side-effects alongside the app, without placing a
 * streaming <Suspense> boundary above the page tree. `children` render as a
 * sibling of the boundary, so page-level notFound()/redirect() calls keep
 * their correct HTTP status (see `AnalyticsTracker`).
 */
export function AnalyticsProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Suspense fallback={null}><AnalyticsTracker /></Suspense>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <>{t1}{children}</>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}